/**
 * Workouts service layer.
 * Abstracts all Firestore operations for workouts from the rest of the application.
 * Handles workout CRUD, templates, stats aggregation, and date-range queries.
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const WORKOUTS_COLLECTION = 'workouts';

/**
 * Serialize a Firestore workout document snapshot into a plain object.
 * Converts Firestore Timestamps to JS Date ISO strings.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object} A plain workout object.
 */
const serializeWorkout = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    userId: data.userId || '',
    title: data.title || '',
    workoutType: data.workoutType || 'other',
    exercises: data.exercises || [],
    duration: data.duration || 0,
    caloriesBurned: data.caloriesBurned || 0,
    notes: data.notes || '',
    sharedAsPostId: data.sharedAsPostId || null,
    templateId: data.templateId || null,
    isTemplate: data.isTemplate || false,
    completedAt: data.completedAt instanceof Timestamp
      ? data.completedAt.toDate().toISOString()
      : data.completedAt || new Date().toISOString(),
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
    _docSnapshot: docSnap,
  };
};

/**
 * Create a new completed workout in Firestore.
 * @param {Object} workoutData - The workout data to save.
 * @param {string} workoutData.userId - The user's ID.
 * @param {string} workoutData.title - Workout title.
 * @param {string} workoutData.workoutType - Type of workout.
 * @param {Array} workoutData.exercises - Array of exercise objects.
 * @param {number} workoutData.duration - Duration in minutes.
 * @param {number} workoutData.caloriesBurned - Estimated calories burned.
 * @param {string} [workoutData.notes] - Optional notes.
 * @param {string} [workoutData.templateId] - Template ID if created from template.
 * @returns {Promise<Object>} The created workout object.
 */
export const createWorkout = async (workoutData) => {
  try {
    const workoutRef = doc(collection(db, WORKOUTS_COLLECTION));
    const workoutId = workoutRef.id;

    const data = {
      userId: workoutData.userId,
      title: workoutData.title || 'Workout',
      workoutType: workoutData.workoutType || 'other',
      exercises: workoutData.exercises || [],
      duration: workoutData.duration || 0,
      caloriesBurned: workoutData.caloriesBurned || 0,
      notes: workoutData.notes || '',
      sharedAsPostId: workoutData.sharedAsPostId || null,
      templateId: workoutData.templateId || null,
      isTemplate: false,
      completedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };

    await setDoc(workoutRef, data);

    return {
      id: workoutId,
      ...data,
      completedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      _docSnapshot: null,
    };
  } catch (error) {
    handleServiceError(error, 'Create workout', 'Failed to save workout. Please try again.');
  }
};

/**
 * Get paginated workout history for a user.
 * @param {string} userId - The user's ID.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=20] - Max workouts per page.
 * @returns {Promise<{workouts: Array, lastDoc: any, hasMore: boolean}>}
 */
export const getWorkouts = async (userId, lastDoc = null, pageLimit = 20) => {
  try {
    const workoutsRef = collection(db, WORKOUTS_COLLECTION);
    let q;

    const constraints = [
      where('userId', '==', userId),
      where('isTemplate', '==', false),
      orderBy('completedAt', 'desc'),
    ];

    if (lastDoc) {
      q = query(workoutsRef, ...constraints, startAfter(lastDoc), firestoreLimit(pageLimit));
    } else {
      q = query(workoutsRef, ...constraints, firestoreLimit(pageLimit));
    }

    const querySnapshot = await getDocs(q);
    const workouts = querySnapshot.docs.map(serializeWorkout);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { workouts, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get workouts', 'Failed to load workouts. Please try again.');
  }
};

/**
 * Get a single workout by ID.
 * @param {string} workoutId - The workout document ID.
 * @returns {Promise<Object|null>} The workout object or null.
 */
export const getWorkoutById = async (workoutId) => {
  try {
    const docRef = doc(db, WORKOUTS_COLLECTION, workoutId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return serializeWorkout(docSnap);
  } catch (error) {
    handleServiceError(error, 'Get workout', 'Failed to load workout. Please try again.');
  }
};

/**
 * Delete a workout by ID.
 * @param {string} workoutId - The workout document ID.
 * @returns {Promise<string>} The deleted workout ID.
 */
export const deleteWorkout = async (workoutId) => {
  try {
    const docRef = doc(db, WORKOUTS_COLLECTION, workoutId);
    await deleteDoc(docRef);
    return workoutId;
  } catch (error) {
    handleServiceError(error, 'Delete workout', 'Failed to delete workout. Please try again.');
  }
};

/**
 * Update a workout's sharedAsPostId after sharing to feed.
 * @param {string} workoutId - The workout document ID.
 * @param {string} postId - The post ID it was shared as.
 * @returns {Promise<void>}
 */
export const updateWorkoutSharedPost = async (workoutId, postId) => {
  try {
    const docRef = doc(db, WORKOUTS_COLLECTION, workoutId);
    await updateDoc(docRef, { sharedAsPostId: postId });
  } catch (error) {
    handleServiceError(error, 'Update workout', 'Failed to update workout. Please try again.');
  }
};

/**
 * Get aggregate stats for a user's workouts.
 * @param {string} userId - The user's ID.
 * @returns {Promise<Object>} Stats object with totalWorkouts, totalDuration, thisWeekCount, currentStreak.
 */
export const getWorkoutStats = async (userId) => {
  try {
    const workoutsRef = collection(db, WORKOUTS_COLLECTION);
    const q = query(
      workoutsRef,
      where('userId', '==', userId),
      where('isTemplate', '==', false),
      orderBy('completedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const workouts = querySnapshot.docs.map(serializeWorkout);

    // Total workouts
    const totalWorkouts = workouts.length;

    // Total duration
    const totalDuration = workouts.reduce((sum, w) => sum + (w.duration || 0), 0);

    // This week's count
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const thisWeekCount = workouts.filter((w) => {
      const date = new Date(w.completedAt);
      return date >= startOfWeek;
    }).length;

    // Current streak (consecutive days with workouts)
    let currentStreak = 0;
    if (workouts.length > 0) {
      const workoutDates = new Set(
        workouts.map((w) => {
          const d = new Date(w.completedAt);
          return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        })
      );

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Check if user worked out today or yesterday to start streak
      const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayKey = `${yesterday.getFullYear()}-${yesterday.getMonth()}-${yesterday.getDate()}`;

      if (workoutDates.has(todayKey) || workoutDates.has(yesterdayKey)) {
        let checkDate = workoutDates.has(todayKey) ? new Date(today) : new Date(yesterday);
        while (true) {
          const key = `${checkDate.getFullYear()}-${checkDate.getMonth()}-${checkDate.getDate()}`;
          if (workoutDates.has(key)) {
            currentStreak++;
            checkDate.setDate(checkDate.getDate() - 1);
          } else {
            break;
          }
        }
      }
    }

    // Average duration
    const avgDuration = totalWorkouts > 0 ? Math.round(totalDuration / totalWorkouts) : 0;

    return {
      totalWorkouts,
      totalDuration,
      thisWeekCount,
      currentStreak,
      avgDuration,
    };
  } catch (error) {
    handleServiceError(error, 'Get workout stats', 'Failed to load workout stats.');
  }
};

/**
 * Get workouts within a date range for calendar and chart views.
 * @param {string} userId - The user's ID.
 * @param {Date} startDate - Start date.
 * @param {Date} endDate - End date.
 * @returns {Promise<Array>} Array of workout objects.
 */
export const getWorkoutsByDateRange = async (userId, startDate, endDate) => {
  try {
    const workoutsRef = collection(db, WORKOUTS_COLLECTION);
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const q = query(
      workoutsRef,
      where('userId', '==', userId),
      where('isTemplate', '==', false),
      where('completedAt', '>=', startTimestamp),
      where('completedAt', '<=', endTimestamp),
      orderBy('completedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(serializeWorkout);
  } catch (error) {
    handleServiceError(error, 'Get workouts by date range', 'Failed to load workouts.');
  }
};

/**
 * Save a workout as a reusable template.
 * @param {Object} workoutData - The template data.
 * @returns {Promise<Object>} The created template object.
 */
export const saveTemplate = async (workoutData) => {
  try {
    const templateRef = doc(collection(db, WORKOUTS_COLLECTION));
    const templateId = templateRef.id;

    const data = {
      userId: workoutData.userId,
      title: workoutData.title || 'My Template',
      workoutType: workoutData.workoutType || 'strength',
      exercises: (workoutData.exercises || []).map((ex) => ({
        name: ex.name,
        sets: ex.sets.map((s) => ({
          reps: s.reps || 0,
          weight: s.weight || 0,
          unit: s.unit || 'lbs',
          completed: false,
        })),
        notes: ex.notes || '',
        restTime: ex.restTime || 60,
      })),
      duration: 0,
      caloriesBurned: 0,
      notes: workoutData.notes || '',
      sharedAsPostId: null,
      templateId: null,
      isTemplate: true,
      completedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };

    await setDoc(templateRef, data);

    return {
      id: templateId,
      ...data,
      completedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      _docSnapshot: null,
    };
  } catch (error) {
    handleServiceError(error, 'Save template', 'Failed to save template. Please try again.');
  }
};

/**
 * Get user's workout templates.
 * @param {string} userId - The user's ID.
 * @returns {Promise<Array>} Array of template objects.
 */
export const getTemplates = async (userId) => {
  try {
    const workoutsRef = collection(db, WORKOUTS_COLLECTION);
    const q = query(
      workoutsRef,
      where('userId', '==', userId),
      where('isTemplate', '==', true),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(serializeWorkout);
  } catch (error) {
    handleServiceError(error, 'Get templates', 'Failed to load templates.');
    return [];
  }
};

/**
 * Delete a template.
 * @param {string} templateId - The template document ID.
 * @returns {Promise<string>} The deleted template ID.
 */
export const deleteTemplate = async (templateId) => {
  try {
    const docRef = doc(db, WORKOUTS_COLLECTION, templateId);
    await deleteDoc(docRef);
    return templateId;
  } catch (error) {
    handleServiceError(error, 'Delete template', 'Failed to delete template. Please try again.');
  }
};

/**
 * Get popular community templates (templates from any user, sorted by most recent).
 * @param {number} [pageLimit=10] - Max templates to return.
 * @returns {Promise<Array>} Array of template objects.
 */
export const getPopularTemplates = async (pageLimit = 10) => {
  try {
    const workoutsRef = collection(db, WORKOUTS_COLLECTION);
    const q = query(
      workoutsRef,
      where('isTemplate', '==', true),
      orderBy('createdAt', 'desc'),
      firestoreLimit(pageLimit)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(serializeWorkout);
  } catch (error) {
    handleServiceError(error, 'Get popular templates', 'Failed to load community templates.');
    return [];
  }
};
